/** HistoryTable + OperationDetailDrawer regroupent events par operation_id. */
import React, { useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import OperationDetailDrawer from "./OperationDetailDrawer";

function groupOps(events) {
  const byOp = new Map();
  const nullOps = [];
  for (const e of events) {
    if (!e.operation_id) {
      // Standalone events (uploaded, validated, dry_run, access_denied, baseline_seeded)
      nullOps.push(e);
      continue;
    }
    if (!byOp.has(e.operation_id)) byOp.set(e.operation_id, []);
    byOp.get(e.operation_id).push(e);
  }
  const ops = [];
  for (const [opId, es] of byOp.entries()) {
    const sorted = [...es].sort((a, b) => a.at.localeCompare(b.at));
    const types = new Set(sorted.map((e) => e.event_type));
    const startedInstall = types.has("install_started");
    const startedRollback = types.has("rollback_started");
    const type = startedInstall ? "install" : (startedRollback ? "rollback" : "other");
    // outcome = last terminal event
    const terminals = ["installed", "install_failed", "rolled_back", "rollback_failed"];
    let outcome = null;
    for (let i = sorted.length - 1; i >= 0; i--) { if (terminals.includes(sorted[i].event_type)) { outcome = sorted[i].event_type; break; } }
    // rollback source: auto if this op mixed install_started + install_failed + rolled_back; manual if only rollback_started.
    let rollback_source = null;
    if (outcome === "rolled_back") {
      rollback_source = types.has("install_started") ? "auto" : "manuel";
    }
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    ops.push({
      operation_id: opId,
      type, outcome, rollback_source,
      started_at: first.at,
      ended_at: last.at,
      duration_ms: new Date(last.at) - new Date(first.at),
      version_from: first.version_from,
      version_to: first.version_to,
      actor: first.actor,
      events: sorted,
    });
  }
  // Include baseline_seeded as its own row for readability
  for (const e of nullOps) {
    if (e.event_type === "baseline_seeded") {
      ops.push({
        operation_id: "baseline_" + e.event_id.slice(0, 8),
        type: "baseline", outcome: "baseline_seeded",
        started_at: e.at, ended_at: e.at, duration_ms: 0,
        version_from: e.version_from, version_to: e.version_to,
        actor: e.actor,
        events: [e],
      });
    }
  }
  ops.sort((a, b) => b.started_at.localeCompare(a.started_at));
  return ops;
}

function badgeFor(op) {
  if (op.type === "baseline") return { className: "bg-gray-200 text-gray-700", label: "Baseline" };
  if (op.type === "install" && op.outcome === "installed") return { className: "bg-green-100 text-green-700", label: "Installation réussie" };
  if (op.type === "install" && op.outcome === "install_failed") return { className: "bg-red-100 text-red-700", label: "Installation échouée" };
  if (op.outcome === "rolled_back" && op.rollback_source === "auto") return { className: "bg-orange-100 text-orange-800", label: "⚙️ Rollback automatique" };
  if (op.outcome === "rolled_back" && op.rollback_source === "manuel") return { className: "bg-blue-100 text-blue-700", label: "Rollback manuel" };
  if (op.outcome === "rollback_failed") return { className: "bg-red-100 text-red-700", label: "Rollback échoué" };
  return { className: "bg-gray-100 text-gray-700", label: op.outcome || "?" };
}

export default function HistoryTable({ events }) {
  const [selected, setSelected] = useState(null);
  const ops = useMemo(() => groupOps(events || []), [events]);
  if (!events || events.length === 0) {
    return <Card className="p-6 text-center text-muted-foreground">Aucune opération à afficher.</Card>;
  }
  return (
    <>
      <Card className="p-0 overflow-hidden" data-testid="history-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Versions</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Durée</TableHead>
              <TableHead>Initié par</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ops.map((op) => {
              const b = badgeFor(op);
              return (
                <TableRow key={op.operation_id} className="cursor-pointer hover:bg-secondary/60" onClick={() => setSelected(op)} data-testid="history-row" data-outcome={op.type === "baseline" ? "baseline" : op.outcome === "installed" ? "installed" : op.outcome === "install_failed" ? "failed" : (op.outcome === "rolled_back" && op.rollback_source === "auto") ? "rolled_back_auto" : (op.outcome === "rolled_back" && op.rollback_source === "manuel") ? "rolled_back_manual" : op.outcome || "unknown"}>
                  <TableCell><Badge className={b.className}>{b.label}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{op.version_from || "—"} → {op.version_to || "—"}</TableCell>
                  <TableCell className="text-xs">{new Date(op.started_at).toLocaleString()}</TableCell>
                  <TableCell className="text-xs">{Math.round(op.duration_ms / 100) / 10}s</TableCell>
                  <TableCell className="text-xs">{op.actor || "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      <OperationDetailDrawer op={selected} onClose={() => setSelected(null)} />
    </>
  );
}
